import React, { useState, useEffect } from 'react';
import { 
    Table, 
    Card, 
    Button, 
    message, 
    Modal, 
    Form, 
    Input, 
    Select, 
    InputNumber,
    Switch,
    Space,
    Typography,
    Popconfirm,
    Collapse,
    Tag
} from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { getDNSRecords, createDNSRecord, updateDNSRecord, deleteDNSRecord } from '../../services/api';
import { useMediaQuery } from 'react-responsive';

const { Title, Text } = Typography;
const { Option } = Select;
const { Panel } = Collapse;

const DNSRecords = ({ domain }) => {
    const [form] = Form.useForm();
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const isMobile = useMediaQuery({ maxWidth: 768 });

    useEffect(() => {
        if (domain?.id) {
            fetchRecords();
        }
    }, [domain]);

    const fetchRecords = async () => {
        setLoading(true);
        try {
            const data = await getDNSRecords(domain.id);
            setRecords(data);
        } catch (error) {
            console.error('Error fetching DNS records:', error);
            message.error('Failed to fetch DNS records');
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = () => {
        form.resetFields();
        setEditingRecord(null);
        setModalVisible(true);
    };

    const handleEdit = (record) => {
        form.setFieldsValue({
            type: record.type,
            name: record.name,
            content: record.content,
            ttl: record.ttl,
            priority: record.priority,
            proxied: record.proxied
        });
        setEditingRecord(record);
        setModalVisible(true);
    };

    const handleDelete = async (recordId) => {
        try {
            await deleteDNSRecord(domain.id, recordId);
            message.success('DNS record deleted successfully');
            fetchRecords();
        } catch (error) {
            console.error('Error deleting DNS record:', error);
            message.error('Failed to delete DNS record');
        }
    };

    const handleSubmit = async (values) => {
        try {
            if (editingRecord) {
                await updateDNSRecord(domain.id, editingRecord.id, values);
                message.success('DNS record updated successfully');
            } else {
                await createDNSRecord(domain.id, values);
                message.success('DNS record created successfully');
            }
            setModalVisible(false);
            fetchRecords();
        } catch (error) {
            console.error('Error saving DNS record:', error);
            message.error('Failed to save DNS record');
        }
    };

    const mobileColumns = [
        {
            title: 'DNS Record',
            key: 'record',
            render: (record) => (
                <Space direction="vertical" size="small" className="w-full">
                    <Space className="w-full justify-between">
                        <Tag color="blue">{record.type}</Tag>
                        <Space>
                            <Button
                                icon={<EditOutlined />}
                                onClick={() => handleEdit(record)}
                                size="small"
                            />
                            <Popconfirm
                                title="Delete this record?"
                                onConfirm={() => handleDelete(record.id)}
                                placement="topRight"
                            >
                                <Button
                                    icon={<DeleteOutlined />}
                                    danger
                                    size="small"
                                />
                            </Popconfirm>
                        </Space>
                    </Space>
                    <div>
                        <Text strong>Name: </Text>
                        <Text>{record.name}</Text>
                    </div>
                    <div>
                        <Text strong>Content: </Text>
                        <Text>{record.content}</Text>
                    </div>
                    <div className="text-xs text-gray-500">
                        <Space split={<Text type="secondary">|</Text>}>
                            <span>TTL: {record.ttl === 1 ? 'Auto' : record.ttl}</span>
                            {record.priority && <span>Priority: {record.priority}</span>}
                            <span>Proxied: {record.proxied ? 'Yes' : 'No'}</span>
                        </Space>
                    </div>
                </Space>
            ),
        }
    ];

    const desktopColumns = [
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            width: 100,
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Content',
            dataIndex: 'content',
            key: 'content',
        },
        {
            title: 'TTL',
            dataIndex: 'ttl',
            key: 'ttl',
            width: 100,
            render: ttl => ttl === 1 ? 'Auto' : ttl,
        },
        {
            title: 'Priority',
            dataIndex: 'priority',
            key: 'priority',
            width: 100,
            render: priority => priority || '-',
        },
        {
            title: 'Proxied',
            dataIndex: 'proxied',
            key: 'proxied',
            width: 100,
            render: proxied => proxied ? 'Yes' : 'No',
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 150,
            render: (_, record) => (
                <Space>
                    <Button
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                        size="small"
                    />
                    <Popconfirm
                        title="Delete this record?"
                        onConfirm={() => handleDelete(record.id)}
                    >
                        <Button
                            icon={<DeleteOutlined />}
                            danger
                            size="small"
                        />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <Card 
            title={
                <Space direction={isMobile ? "vertical" : "horizontal"} className="w-full">
                    <Title level={isMobile ? 4 : 3}>DNS Records</Title>
                    <Text type="secondary">{domain?.name}</Text>
                </Space>
            }
            extra={
                <Button 
                    type="primary" 
                    icon={<PlusOutlined />}
                    onClick={handleAdd}
                    size={isMobile ? "middle" : "large"}
                >
                    Add Record
                </Button>
            }
            className="overflow-hidden"
        >
            <Table
                columns={isMobile ? mobileColumns : desktopColumns}
                dataSource={records}
                rowKey="id"
                loading={loading}
                pagination={{
                    simple: isMobile,
                    pageSize: isMobile ? 5 : 10,
                }}
                className={isMobile ? "ant-table-mobile" : ""}
            />

            <Modal
                title={`${editingRecord ? 'Edit' : 'Add'} DNS Record`}
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={null}
                width={isMobile ? "100%" : 520}
                style={isMobile ? { top: 0, margin: 0, maxWidth: '100vw' } : {}}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    className="mt-4"
                >
                    <Form.Item
                        name="type"
                        label="Record Type"
                        rules={[{ required: true }]}
                    >
                        <Select size={isMobile ? "large" : "middle"}>
                            <Option value="A">A</Option>
                            <Option value="AAAA">AAAA</Option>
                            <Option value="CNAME">CNAME</Option>
                            <Option value="MX">MX</Option>
                            <Option value="TXT">TXT</Option>
                            <Option value="NS">NS</Option>
                            <Option value="SRV">SRV</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="name"
                        label="Name"
                        rules={[{ required: true }]}
                        tooltip={isMobile ? {
                            title: 'Subdomain name (without domain)',
                            icon: <InfoCircleOutlined />
                        } : null}
                    >
                        <Input 
                            addonAfter={`.${domain?.name}`}
                            size={isMobile ? "large" : "middle"}
                        />
                    </Form.Item>

                    <Form.Item
                        name="content"
                        label="Content"
                        rules={[{ required: true }]}
                    >
                        <Input size={isMobile ? "large" : "middle"} />
                    </Form.Item>

                    <Form.Item
                        name="ttl"
                        label="TTL"
                        initialValue={1}
                    >
                        <Select size={isMobile ? "large" : "middle"}>
                            <Option value={1}>Auto</Option>
                            <Option value={60}>1 minute</Option>
                            <Option value={300}>5 minutes</Option>
                            <Option value={3600}>1 hour</Option>
                            <Option value={86400}>1 day</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) => prevValues.type !== currentValues.type}
                    >
                        {({ getFieldValue }) => (
                            getFieldValue('type') === 'MX' && (
                                <Form.Item
                                    name="priority"
                                    label="Priority"
                                    rules={[{ required: true }]}
                                >
                                    <InputNumber 
                                        min={0} 
                                        max={65535}
                                        size={isMobile ? "large" : "middle"}
                                        className="w-full"
                                    />
                                </Form.Item>
                            )
                        )}
                    </Form.Item>

                    <Form.Item
                        name="proxied"
                        label="Proxy through Cloudflare"
                        valuePropName="checked"
                        initialValue={false}
                    >
                        <Switch />
                    </Form.Item>

                    <Form.Item className="mb-0">
                        <Space className="w-full" direction={isMobile ? "vertical" : "horizontal"}>
                            <Button 
                                type="primary" 
                                htmlType="submit"
                                size={isMobile ? "large" : "middle"}
                                block={isMobile}
                            >
                                {editingRecord ? 'Update' : 'Create'}
                            </Button>
                            <Button 
                                onClick={() => setModalVisible(false)}
                                size={isMobile ? "large" : "middle"}
                                block={isMobile}
                            >
                                Cancel
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    );
};

export default DNSRecords;